/**
 * Statute of Limitations (SOL) utility functions.
 *
 * Calculates days remaining and assigns a severity level used
 * across the Matters list and Matter detail views.
 */

import { differenceInCalendarDays, isValid,parse } from 'date-fns';

export type SolSeverity = 'critical' | 'urgent' | 'warning' | 'safe' | 'none';

export const SOL_THRESHOLDS = {
  CRITICAL: 7,
  URGENT: 30,
  WARNING: 60,
} as const;

/**
 * Parse an SOL date string (YYYY-MM-DD) to a Date object.
 * Returns null for invalid/empty values.
 */
export function parseSolDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
  if (!isValid(parsed)) {
    const fallback = new Date(dateString);
    return isValid(fallback) ? fallback : null;
  }
  return parsed;
}

/**
 * Calculate the number of calendar days remaining until the SOL date.
 * Returns null if the date is invalid or missing.
 */
export function getDaysUntilSol(
  solDate: string | null | undefined,
): number | null {
  const parsed = parseSolDate(solDate);
  if (!parsed) return null;
  return differenceInCalendarDays(parsed, new Date());
}

/**
 * Determine the severity level for an SOL date.
 */
export function getSolSeverity(
  solDate: string | null | undefined,
): SolSeverity {
  const days = getDaysUntilSol(solDate);
  if (days === null) return 'none';
  if (days < SOL_THRESHOLDS.CRITICAL) return 'critical';
  if (days < SOL_THRESHOLDS.URGENT) return 'urgent';
  if (days < SOL_THRESHOLDS.WARNING) return 'warning';
  return 'safe';
}

/**
 * Format days remaining as a human-readable string.
 */
export function formatDaysRemaining(days: number): string {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

/**
 * Style classes for SOL severity in different contexts.
 */
export const SOL_SEVERITY_STYLES = {
  // For Pill / badge in matter detail header
  chip: {
    critical: 'bg-red-100 text-red-800 border-red-200',
    urgent: 'bg-red-50 text-red-700 border-red-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    safe: 'bg-transparent border-transparent',
    none: 'bg-zinc-50 text-zinc-500 border-zinc-200',
  },
  // For table cell text
  text: {
    critical: 'text-red-700 font-bold',
    urgent: 'text-red-600 font-semibold',
    warning: 'text-amber-600 font-medium',
    safe: 'text-zinc-700',
    none: 'text-zinc-500',
  },
  // For table row left border accent
  rowBorder: {
    critical: 'border-l-[3px] border-l-red-500',
    urgent: 'border-l-[3px] border-l-red-400',
    warning: '',
    safe: '',
    none: '',
  },
  // For banner on matter detail
  banner: {
    critical:
      'bg-red-50 border-red-200 text-red-800',
    urgent:
      'bg-red-50 border-red-200 text-red-700',
    warning: '',
    safe: '',
    none: '',
  },
} as const;
